import React from 'react'
import { t } from 'i18next'
import UserListCell, {
  ACCEPTED,
  DECLINED,
  NO_RESPONSE,
  OWNER,
  TENTATIVE,
} from '../common/UserListCell'
import styles from './style.module.scss'

interface AttendeeParams {
  ROLE?: string
  CN?: string
  PARTSTAT?: string
}

interface Attendee {
  val: string
  params: AttendeeParams
}

interface CalendarInstance {
  attendees?: Attendee[]
}

interface AttendeeListProps {
  instance: CalendarInstance | null
  onClose: () => void
}

interface UserListCellProps {
  email: string
  name: string | null
  status: number
}

const AttendeeList: React.FC<AttendeeListProps> = ({ instance, onClose }) => {
  let organizer: Attendee | null = null
  let reqParticipants: Attendee[] = []
  let nonParticipants: Attendee[] = []

  instance?.attendees?.forEach((attendee) => {
    if (attendee.params.ROLE === 'ORGANIZER') {
      organizer = attendee
    } else if (attendee.params.ROLE === 'REQ-PARTICIPANT') {
      reqParticipants.push(attendee)
    } else {
      nonParticipants.push(attendee)
    }
  })

  const getAttendeeProps = (attendee: Attendee): UserListCellProps => {
    const email = attendee.val.toLowerCase().startsWith('mailto:')
      ? attendee.val.slice(7)
      : attendee.val

    return {
      email: email,
      name: attendee.params.CN || null,
      status:
        attendee.params.ROLE === 'ORGANIZER'
          ? OWNER
          : attendee.params.PARTSTAT === 'ACCEPTED'
            ? ACCEPTED
            : attendee.params.PARTSTAT === 'DECLINED'
              ? DECLINED
              : attendee.params.PARTSTAT === 'TENTATIVE'
                ? TENTATIVE
                : NO_RESPONSE,
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>{t('calendar.attendees.title')}</h2>
      </div>
      <div className={styles.content}>
        {organizer && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('calendar.attendees.organizer')}</div>
            <UserListCell {...getAttendeeProps(organizer)} />
          </div>
        )}
        {reqParticipants.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('calendar.attendees.required')}</div>
            {reqParticipants.map((attendee) => (
              <UserListCell key={attendee.val} {...getAttendeeProps(attendee)} />
            ))}
          </div>
        )}
        {nonParticipants.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('calendar.attendees.optional')}</div>
            {nonParticipants.map((attendee) => (
              <UserListCell key={attendee.val} {...getAttendeeProps(attendee)} />
            ))}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <button className={styles.closeButton} onClick={onClose}>
          {t('common.button.close')}
        </button>
      </div>
    </div>
  )
}

export default AttendeeList
